import { create } from 'zustand'
import { apiFetch } from '../utils/api'

const useCalendarioStore = create((set) => ({
    isLoading: true,
    error: "",
    partidas: [],
    sucursales: [],
    bloqueos: [],
    canchas: [],
    clientes: [],
    sucursalSeleccionada: null,
    setSucursalSeleccionada: (sucursal) => set({sucursalSeleccionada: sucursal}),
    iniciar: async() => {
        if (localStorage.getItem('isLoggedIn') === 'true') {
            set({ isLoading: true, error: "" });
            try {
                const [branchesResponse, matchesResponse, blocksResponse, courtsResponse, clientsResponse] = await Promise.all([
                    apiFetch("/api/calendar/branches"),
                    apiFetch("/api/calendar"),
                    apiFetch("/api/court-blocks"),
                    apiFetch("/api/courts"),
                    apiFetch("/api/calendar/clients"),
                ]);

                if (!branchesResponse.status) throw new Error(branchesResponse.message || "No se pudieron cargar las sucursales.");
                if (!matchesResponse.status) throw new Error(matchesResponse.message || "No se pudieron cargar las partidas.");
                if (!blocksResponse.status) throw new Error(blocksResponse.message || "No se pudieron cargar los bloqueos.");
                if (!courtsResponse.status) throw new Error(courtsResponse.message || "No se pudieron cargar las canchas.");

                set({
                    sucursales: Array.isArray(branchesResponse.data) ? branchesResponse.data : [],
                    partidas: Array.isArray(matchesResponse.data) ? matchesResponse.data : [],
                    bloqueos: Array.isArray(blocksResponse.data) ? blocksResponse.data : [],
                    canchas: Array.isArray(courtsResponse.data) ? courtsResponse.data : [],
                    clientes: Array.isArray(clientsResponse?.data) ? clientsResponse.data : [],
                });
            } catch (error) {
                console.error("Error iniciando calendario:", error);
                set({
                    partidas: [],
                    sucursales: [],
                    bloqueos: [],
                    canchas: [],
                    clientes: [],
                    error: error.message || "No se pudo cargar el calendario.",
                });
            } finally {
                set({ isLoading: false });
            }
        }
    },
    cargarPartidas: async(idSucursal = -1) => {
        set({isLoading: true, error: ""});
        const url = idSucursal && idSucursal !== -1 ? `/api/calendar?branchId=${idSucursal}` : '/api/calendar';
        try {
            const data = await apiFetch(url);
            if (data.status) {
                set({partidas: Array.isArray(data.data) ? data.data : []});
            } else {
                set({partidas: [], error: data.message || "No se pudieron cargar las partidas."});
            }
        } catch (error) {
            console.error('Error cargando partidas:', error);
            set({partidas: [], error: error.message || "No se pudieron cargar las partidas."});
        } finally {
            set({isLoading: false});
        }
    },
    cargarBloqueos: async () => {
        try {
            const data = await apiFetch("/api/court-blocks");
            if (data.status) {
                set({ bloqueos: Array.isArray(data.data) ? data.data : [] });
            } else {
                set({ bloqueos: [], error: data.message || "No se pudieron cargar los bloqueos." });
            }
        } catch (error) {
            console.error('Error cargando bloqueos:', error);
            set({ bloqueos: [], error: error.message || "No se pudieron cargar los bloqueos." });
        }
    },
    crearBloqueo: async (bloqueoData) => {
        try {
            const data = await apiFetch("/api/court-blocks", {
                method: "POST",
                body: JSON.stringify(bloqueoData)
            });
            return data;
        } catch (error) {
            console.error('Error creando bloqueo:', error);
            return { status: 0, message: "Error de red al crear bloqueo" };
        }
    },
    crearReservacion: async (reservacionData) => {
        try {
            const data = await apiFetch("/api/matches", {
                method: "POST",
                body: JSON.stringify(reservacionData)
            });
            return data;
        } catch (error) {
            console.error('Error creando reservacion:', error);
            return { status: 0, message: "Error de red al crear reservacion" };
        }
    },
    crearCliente: async (clienteData) => {
        try {
            const data = await apiFetch('/api/calendar/clients', {
                method: 'POST',
                body: JSON.stringify(clienteData),
            });
            if (data.status && data.data) {
                set((state) => ({ clientes: [data.data, ...state.clientes.filter((cliente) => cliente.id !== data.data.id)] }));
            }
            return data;
        } catch (error) {
            return { status: false, message: error.message || 'No se pudo crear el cliente.' };
        }
    },
    cancelarReservacion: async (id) => {
        try {
            return await apiFetch(`/api/matches/${id}/cancel`, {
                method: "POST",
            });
        } catch (error) {
            console.error('Error cancelando reservacion:', error);
            return { status: 0, message: "Error de red al cancelar la reservación" };
        }
    },
    eliminarBloqueo: async (id) => {
        try {
            const data = await apiFetch(`/api/court-blocks/${id}`, {
                method: "DELETE"
            });
            return data;
        } catch (error) {
            console.error('Error eliminando bloqueo:', error);
            return { status: 0, message: "Error de red al eliminar bloqueo" };
        }
    },
    aprobarPartida: async (id) => {
        try {
            const data = await apiFetch(`/api/matches/${id}/approve`, {
                method: "POST",
            });
            return data;
        } catch (error) {
            console.error('Error aprobando partida:', error);
            return { status: 0, message: "Error de red al aprobar partida" };
        }
    },
    recordarPagoPartida: async (id) => {
        try {
            const data = await apiFetch(`/api/matches/${id}/payment-reminder`, {
                method: "POST",
            });
            return data;
        } catch (error) {
            console.error('Error enviando recordatorio de pago:', error);
            return { status: 0, message: "Error de red al enviar recordatorio" };
        }
    },
    cargarChatPartida: async (id) => {
        try {
            return await apiFetch(`/api/matches/${id}/chat`);
        } catch (error) {
            console.error('Error cargando chat de partida:', error);
            return { status: 0, message: "Error de red al cargar chat" };
        }
    },
    enviarChatPartida: async (id, text) => {
        try {
            return await apiFetch(`/api/matches/${id}/chat`, {
                method: "POST",
                body: JSON.stringify({ text })
            });
        } catch (error) {
            console.error('Error enviando mensaje de staff:', error);
            return { status: 0, message: "Error de red al enviar mensaje" };
        }
    }
}))

export default useCalendarioStore
